import { getAccessToken } from '../utils/storage';
import { authFetch, getApiUrl } from '../utils/api';
import { friendlyApiError } from '../utils/apiErrors';

export interface StorePerformanceRow {
  data_source?: 'raw' | string | null;
  date?: string | null;
  branch_code?: string | null;
  branch_name?: string | null;
  rage_sqm?: number | string | null;
  province?: string | null;
  district?: string | null;
  status?: string | null;
  type?: string | null;
  mall_type?: string | null;
  target_ex_vat?: number | string | null;
  turnover_ex_vat?: number | string | null;
  turnover_per_sqm?: number | string | null;
  rent_ex_vat?: number | string | null;
  rent_per_sqm?: number | string | null;
  salaries?: number | string | null;
  wages?: number | string | null;
  utilities_ex_vat?: number | string | null;
  fixed_charges_ex_vat?: number | string | null;
  variable_charges_ex_vat?: number | string | null;
  other_occupancy_cost_ex_vat?: number | string | null;
  total_cheque_paid_ex_vat?: number | string | null;
  profit_loss?: number | string | null;
  transactions?: number | string | null;
  stock_on_hand?: number | string | null;
  stock_on_hand_per_sqm?: number | string | null;
  rtto?: number | string | null;
  r_utto?: number | string | null;
  tcotto?: number | string | null;
  tctto?: number | string | null;
  rdttd?: number | string | null;
  ttsd?: number | string | null;
  rtsd?: number | string | null;
  actual_gp_percent?: number | string | null;
  gp_percent_used?: number | string | null;
  gp_calculation_mode?: 'actual' | 'adjustment' | 'disabled' | string | null;
  first_trade_date?: string | null;
  last_trade_date?: string | null;
  active_status?: boolean | number | null;
  [key: string]: unknown;
}

export interface StorePerformanceColumn {
  key: string;
  label: string;
}

interface StorePerformanceResponse {
  rows: StorePerformanceRow[];
  columns?: StorePerformanceColumn[];
}

interface StorePerformanceMonthsResponse {
  months: string[];
}

export interface StorePerformanceCalculationOptions {
  gpAdjustmentPercent?: number;
  useActualGp?: boolean;
  useGpAdjustment?: boolean;
  includeStockMetrics?: boolean;
}

const makeStorePerformanceHeaders = (): Record<string, string> => {
  const token = getAccessToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  return headers;
};

export const fetchStorePerformanceMonths = async (): Promise<string[]> => {
  const response = await authFetch(getApiUrl('/v1/store-performance/months?limit=120'), {
    method: 'GET',
    headers: makeStorePerformanceHeaders(),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(friendlyApiError(response.status, errorData.detail, 'The Store Performance report'));
  }

  const data: StorePerformanceMonthsResponse = await response.json();
  return Array.isArray(data.months) ? data.months : [];
};

export interface FinancialSummaryData {
  stores: number;
  target: number | null;
  turnover: number | null;
  turnover_per_sqm: number | null;
  rent: number | null;
  rent_per_sqm: number | null;
  salaries: number | null;
  wages: number | null;
  utilities: number | null;
  fixed_charges: number | null;
  variable_charges: number | null;
  total_cheque: number | null;
  profit_loss: number | null;
  transactions: number | null;
  ttsd: number | null;
  rtsd: number | null;
}

export interface StorePerformanceAveragesResponse {
  branch_3m: FinancialSummaryData;
  branch_12m: FinancialSummaryData;
  province_avg: FinancialSummaryData;
  province_name: string;
  all_stores_avg: FinancialSummaryData;
  all_stores_total_turnover: number | null;
  all_stores_total_profit_loss: number | null;
}

export const fetchStorePerformanceAverages = async (
  branchCode: string,
  month: string | undefined,
  options: StorePerformanceCalculationOptions = {}
): Promise<StorePerformanceAveragesResponse> => {
  const params = new URLSearchParams({ branch_code: branchCode, limit: '5000' });
  if (month) params.set('month', month);
  addCalculationParams(params, options);

  const response = await authFetch(
    getApiUrl(`/v1/store-performance/averages?${params.toString()}`),
    { method: 'GET', headers: makeStorePerformanceHeaders() }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(friendlyApiError(response.status, errorData.detail, 'The Store Performance report'));
  }

  return response.json();
};

export interface SegmentSummaryData extends FinancialSummaryData {
  trade_density: number | null;
}

export interface SegmentPeriodData {
  current: SegmentSummaryData;
  avg_12m: SegmentSummaryData;
}

export interface StorePerformanceSegmentResponse {
  mall_type: SegmentPeriodData;
  province: SegmentPeriodData;
}

export interface NewStoresMonth {
  key: string;
  label: string;
}

export interface NewStoresReportRow {
  branch_code: string;
  branch_name?: string | null;
  values: Record<string, number | string | null>;
  total: number | string | null;
}

export interface NewStoresSeriesPoint {
  branch_code: string;
  date: string;
  month: string;
  month_label: string;
  value: number | string | null;
}

export interface StorePerformanceNewStoresResponse {
  metric: 'turnover' | 'rent' | 'rtto' | string;
  date_start: string;
  date_end: string;
  first_trade_start: string;
  first_trade_end: string;
  months: NewStoresMonth[];
  rows: NewStoresReportRow[];
  series: NewStoresSeriesPoint[];
  new_store_count: number;
  total: number | string | null;
}

export const fetchStorePerformanceNewStores = async (
  metric: 'turnover' | 'rent' | 'rtto',
  branchCode?: string,
  asOfDate?: string
): Promise<StorePerformanceNewStoresResponse> => {
  const params = new URLSearchParams({ metric, limit: '102' });
  if (branchCode && branchCode !== 'All') params.set('branch_code', branchCode);
  if (asOfDate) params.set('as_of_date', asOfDate);

  const response = await authFetch(
    getApiUrl(`/v1/store-performance/new-stores?${params.toString()}`),
    { method: 'GET', headers: makeStorePerformanceHeaders() }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(friendlyApiError(response.status, errorData.detail, 'The Store Performance report'));
  }

  return response.json();
};

export const fetchStorePerformanceSegmentAverages = async (
  month: string | undefined,
  mallType: string | undefined,
  province: string | undefined,
  options: StorePerformanceCalculationOptions = {}
): Promise<StorePerformanceSegmentResponse> => {
  const params = new URLSearchParams({ limit: '5000' });
  if (month) params.set('month', month);
  if (mallType) params.set('mall_type', mallType);
  if (province) params.set('province', province);
  addCalculationParams(params, options);

  const response = await authFetch(
    getApiUrl(`/v1/store-performance/segment-averages?${params.toString()}`),
    { method: 'GET', headers: makeStorePerformanceHeaders() }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(friendlyApiError(response.status, errorData.detail, 'The Store Performance report'));
  }

  return response.json();
};

export interface BranchTrendRow {
  date: string;
  turnover_ex_vat: number | null;
  rtto: number | null;
  r_utto: number | null;
  tcotto: number | null;
  tctto: number | null;
  rdttd: number | null;
}

export const fetchStorePerformanceBranchTrend = async (
  branchCode: string
): Promise<BranchTrendRow[]> => {
  const params = new URLSearchParams({ branch_code: branchCode });
  const response = await authFetch(
    getApiUrl(`/v1/store-performance/branch-trend?${params.toString()}`),
    { method: 'GET', headers: makeStorePerformanceHeaders() }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(friendlyApiError(response.status, errorData.detail, 'The Store Performance report'));
  }

  const data = await response.json();
  return Array.isArray(data.rows) ? data.rows : [];
};

export interface ForecastAdjustmentParameters {
  turnoverPercent?: number;
  interestIncomePercent?: number;
  badDebtsPercent?: number;
  rentPercent?: number;
  utilitiesPercent?: number;
  otherCostsPercent?: number;
  salariesPercent?: number;
  wagesPercent?: number;
  hoCostsPercent?: number;
  depreciationPercent?: number;
  hoSalariesPercent?: number;
}

export interface StoreForecastRow {
  branch_code: string;
  branch_name: string | null;
  rage_sqm: number | null;
  actual_gp_percent: number | null;
  turnover: number | null;
  rent: number | null;
  utilities: number | null;
  other_costs: number | null;
  salaries: number | null;
  wages: number | null;
  transactions: number | null;
  ho_costs: number | null;
  bad_debts: number | null;
  interest_income: number | null;
  depreciation: number | null;
  ho_salaries: number | null;
  gp_adjustment: number | null;
  profit_before_adjustments: number | null;
  profit_before_headoffice_costing: number | null;
  profit_after_headoffice_costing: number | null;
  trade_density: number | null;
  turnover_projected: number | null;
  interest_income_projected: number | null;
  bad_debts_projected: number | null;
  rent_projected: number | null;
  utilities_projected: number | null;
  other_costs_projected: number | null;
  salaries_projected: number | null;
  wages_projected: number | null;
  ho_costs_projected: number | null;
  depreciation_projected: number | null;
  ho_salaries_projected: number | null;
  profit_before_adjustments_projected: number | null;
  profit_before_headoffice_costing_projected: number | null;
  profit_after_headoffice_costing_projected: number | null;
  trade_density_projected: number | null;
}

export const fetchStorePerformanceForecast = async (
  month: string | undefined,
  options: StorePerformanceCalculationOptions = {},
  adjustments: ForecastAdjustmentParameters = {}
): Promise<StoreForecastRow[]> => {
  const params = new URLSearchParams({ limit: '5000' });
  if (month) params.set('month', month);
  addCalculationParams(params, options);
  params.set('turnover_percent', String(adjustments.turnoverPercent ?? 0));
  params.set('interest_income_percent', String(adjustments.interestIncomePercent ?? 0));
  params.set('bad_debts_percent', String(adjustments.badDebtsPercent ?? 0));
  params.set('rent_percent', String(adjustments.rentPercent ?? 0));
  params.set('utilities_percent', String(adjustments.utilitiesPercent ?? 0));
  params.set('other_costs_percent', String(adjustments.otherCostsPercent ?? 0));
  params.set('salaries_percent', String(adjustments.salariesPercent ?? 0));
  params.set('wages_percent', String(adjustments.wagesPercent ?? 0));
  params.set('ho_costs_percent', String(adjustments.hoCostsPercent ?? 0));
  params.set('depreciation_percent', String(adjustments.depreciationPercent ?? 0));
  params.set('ho_salaries_percent', String(adjustments.hoSalariesPercent ?? 0));

  const response = await authFetch(
    getApiUrl(`/v1/store-performance/forecast?${params.toString()}`),
    { method: 'GET', headers: makeStorePerformanceHeaders() }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(friendlyApiError(response.status, errorData.detail, 'The Store Performance report'));
  }

  const data = await response.json();
  return Array.isArray(data.rows) ? data.rows : [];
};

const addCalculationParams = (
  params: URLSearchParams,
  options: StorePerformanceCalculationOptions = {}
) => {
  params.set('gp_adjustment_percent', String(options.gpAdjustmentPercent ?? 50));
  params.set('use_actual_gp', String(options.useActualGp ?? true));
  params.set('use_gp_adjustment', String(options.useGpAdjustment ?? false));
  params.set('include_stock_metrics', String(options.includeStockMetrics ?? false));
};

export const fetchStorePerformanceRows = async (
  limit = 1000,
  month?: string,
  options: StorePerformanceCalculationOptions = {}
): Promise<StorePerformanceRow[]> => {
  const params = new URLSearchParams({ limit: String(limit) });
  if (month) {
    params.set('month', month);
  }
  addCalculationParams(params, options);

  const response = await authFetch(getApiUrl(`/v1/store-performance?${params.toString()}`), {
    method: 'GET',
    headers: makeStorePerformanceHeaders(),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(friendlyApiError(response.status, errorData.detail, 'The Store Performance report'));
  }

  const data: StorePerformanceResponse = await response.json();
  return Array.isArray(data.rows) ? data.rows : [];
};
